import { ALL_GAMES } from "@/data/games";
import { STATES } from "@/data/states";

/**
 * Legacy/indexed URLs of the form `/games/{stateSlug}-{gameSuffix}` point at a
 * state-scoped game whose canonical URL is `/{stateSlug}/{gameSuffix}`.
 * Returns the canonical path when the slug belongs to a state game.
 */
export function canonicalStateGamePath(gameSlug: string): { stateSlug: string; gameSlug: string } | null {
  const game = resolveLegacyStateGame(gameSlug);
  if (!game) return null;
  const state = STATES.find((s) => s.id === game.state_id);
  if (!state) return null;
  const suffix = game.slug.startsWith(`${state.slug}-`)
    ? game.slug.slice(state.slug.length + 1)
    : game.slug;
  return { stateSlug: state.slug, gameSlug: suffix };
}

/**
 * Resolves a legacy `/games/{slug}` slug to the state game it used to point at.
 * Handles three historical shapes:
 *  - the stored slug itself (`illinois-pick-4-midday`)
 *  - the bare game name when only one state uses it (`show-me-cash`)
 *  - a state-abbreviation prefix (`il-pick-4-midday`, `mi-daily-4-evening`)
 * Ambiguous bare names (e.g. `pick-4-day`, used by 7 states) stay unresolved so
 * the URL returns a real 404 instead of an empty page Google flags as soft 404.
 */
function resolveLegacyStateGame(gameSlug: string) {
  const stateGames = ALL_GAMES.filter((g) => !g.is_multistate && g.state_id);
  const exact = stateGames.find((g) => g.slug === gameSlug);
  if (exact) return exact;

  const abbrevMatch = gameSlug.match(/^([a-z]{2})-(.+)$/);
  if (abbrevMatch) {
    const state = STATES.find((s) => s.abbreviation.toLowerCase() === abbrevMatch[1]);
    if (state) {
      const rest = abbrevMatch[2];
      const byAbbrev = stateGames.find(
        (g) => g.state_id === state.id && (g.slug === `${state.slug}-${rest}` || g.slug === rest),
      );
      if (byAbbrev) return byAbbrev;
    }
  }

  const suffixMatches = stateGames.filter((g) => g.slug.endsWith(`-${gameSlug}`));
  return suffixMatches.length === 1 ? suffixMatches[0] : null;
}

/**
 * Canonical URL shapes for a game. State-scoped games live under
 * `/{stateSlug}/{gameSlug}`; multistate games under `/games/{gameSlug}`.
 */
export function gamePaths(gameSlug: string, stateSlug?: string) {
  const base = stateSlug ? `/${stateSlug}/${gameSlug}` : `/games/${gameSlug}`;
  return {
    game: base,
    history: `${base}/history`,
    result: (date: string) => `${base}/results/${date}`,
  };
}

/**
 * For a stored (possibly state-prefixed) game slug, returns the canonical
 * link parts so UI links point at the route that actually exists.
 */
export function canonicalGameLinkParts(storedSlug: string): { gameSlug: string; stateSlug?: string } {
  const canonical = canonicalStateGamePath(storedSlug);
  return canonical ? { gameSlug: canonical.gameSlug, stateSlug: canonical.stateSlug } : { gameSlug: storedSlug };
}

/** Canonical `/…/results/{date}` path for a stored (possibly prefixed) slug. */
export function resultPathForStoredSlug(storedSlug: string, date: string): string {
  const { gameSlug, stateSlug } = canonicalGameLinkParts(storedSlug);
  return gamePaths(gameSlug, stateSlug).result(date);
}
